<template>
  <div class="signup-container">
    <div class="signup-background">
      <div class="background-shapes">
        <div class="shape shape-1"></div>
        <div class="shape shape-2"></div>
        <div class="shape shape-3"></div>
      </div>
    </div>

    <div class="signup-content">
      <div class="signup-card">
        <div class="signup-header">
          <div class="logo">
            <img src="@/assets/amana1.jpeg" alt="Inveep GPAO" class="logo-image" />
            <h1>Inveep GPAO</h1>
          </div>
          <p class="subtitle">Création de votre espace d'entreprise SaaS</p>
        </div>

        <form v-if="!requiresOtpValidation" @submit.prevent="handleSignup" class="signup-form">
          <div v-if="signupError" class="alert alert-danger">
            <i class="fas fa-exclamation-triangle"></i> {{ signupError }}
          </div>

          <!-- Section Entreprise -->
          <div class="form-section">
            <h3><i class="fas fa-building"></i> Informations Entreprise</h3>
            <div class="form-group">
              <label for="entrepriseNom">Nom de l'entreprise</label>
              <div class="input-wrapper">
                <i class="fas fa-briefcase"></i>
                <input
                  id="entrepriseNom"
                  v-model="form.entrepriseNom"
                  type="text"
                  class="form-control"
                  placeholder="Ex: Ma Société SAS"
                  required
                />
              </div>
            </div>

            <div class="form-group">
              <label for="entrepriseEmail">Email de l'entreprise</label>
              <div class="input-wrapper">
                <i class="fas fa-envelope"></i>
                <input
                  id="entrepriseEmail"
                  v-model="form.entrepriseEmail"
                  type="email"
                  class="form-control"
                  placeholder="Ex: contact@entreprise.com"
                  required
                />
              </div>
            </div>
          </div>

          <!-- Section Administrateur -->
          <div class="form-section">
            <h3><i class="fas fa-user-shield"></i> Compte Administrateur</h3>
            <div class="form-row">
              <div class="form-group col">
                <label for="adminPrenom">Prénom</label>
                <div class="input-wrapper">
                  <i class="fas fa-user"></i>
                  <input
                    id="adminPrenom"
                    v-model="form.adminPrenom"
                    type="text"
                    class="form-control"
                    placeholder="Prénom"
                    required
                  />
                </div>
              </div>
              <div class="form-group col">
                <label for="adminNom">Nom</label>
                <div class="input-wrapper">
                  <i class="fas fa-user"></i>
                  <input
                    id="adminNom"
                    v-model="form.adminNom"
                    type="text"
                    class="form-control"
                    placeholder="Nom"
                    required
                  />
                </div>
              </div>
            </div>

            <div class="form-group">
              <label for="adminEmail">Email de connexion</label>
              <div class="input-wrapper">
                <i class="fas fa-envelope"></i>
                <input
                  id="adminEmail"
                  v-model="form.adminEmail"
                  type="email"
                  class="form-control"
                  placeholder="Ex: admin@entreprise.com"
                  required
                />
              </div>
            </div>

            <div class="form-group">
              <label for="adminMotDePasse">Mot de passe</label>
              <div class="input-wrapper">
                <i class="fas fa-lock"></i>
                <input
                  id="adminMotDePasse"
                  v-model="form.adminMotDePasse"
                  :type="showPassword ? 'text' : 'password'"
                  class="form-control"
                  placeholder="Au moins 6 caractères"
                  required
                />
                <button type="button" class="password-toggle" @click="showPassword = !showPassword">
                  <i class="fas" :class="showPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
                </button>
              </div>
            </div>
          </div>

          <button type="submit" class="btn btn-primary btn-block" :disabled="loading">
            <span v-if="loading"><i class="fas fa-spinner fa-spin"></i> Inscription en cours...</span>
            <span v-else><i class="fas fa-user-plus"></i> Créer mon espace</span>
          </button>
        </form>

        <!-- Formulaire de validation OTP -->
        <form v-else @submit.prevent="handleVerifyOtp" class="signup-form">
          <div v-if="signupError" class="alert alert-danger">
            <i class="fas fa-exclamation-triangle"></i> {{ signupError }}
          </div>

          <div class="form-section text-center">
            <h3><i class="fas fa-key"></i> Vérification de sécurité</h3>
            <p style="font-size: 14px; margin-bottom: 20px; color: var(--text-muted);">
              Un code de validation OTP à 6 chiffres a été envoyé par e-mail à :<br>
              <strong>{{ signupEmail }}</strong>
            </p>

            <div class="form-group">
              <label for="otpCode">Entrez le code OTP</label>
              <div class="input-wrapper text-center">
                <i class="fas fa-shield-alt"></i>
                <input
                  id="otpCode"
                  v-model="otpCode"
                  type="text"
                  maxlength="6"
                  class="form-control text-center"
                  placeholder="123456"
                  style="letter-spacing: 8px; font-size: 20px; font-weight: 700;"
                  required
                />
              </div>
            </div>
          </div>

          <button type="submit" class="btn btn-primary btn-block" :disabled="loading">
            <span v-if="loading"><i class="fas fa-spinner fa-spin"></i> Validation...</span>
            <span v-else><i class="fas fa-check-circle"></i> Valider mon compte</span>
          </button>

          <button type="button" class="btn btn-outline btn-block mt-3" @click="handleResendOtp" :disabled="loading">
            <i class="fas fa-sync-alt"></i> Renvoyer le code OTP
          </button>
        </form>

        <div class="signup-footer">
          <p>Vous avez déjà un compte ? <router-link to="/login">Connectez-vous</router-link></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'

export default {
  name: 'Signup',
  setup() {
    const store = useStore()
    const router = useRouter()
    const toast = useToast()

    const form = ref({
      entrepriseNom: '',
      entrepriseEmail: '',
      adminNom: '',
      adminPrenom: '',
      adminEmail: '',
      adminMotDePasse: ''
    })

    const loading = ref(false)
    const showPassword = ref(false)
    const signupError = ref('')
    
    // Variables pour validation OTP
    const requiresOtpValidation = ref(false)
    const otpCode = ref('')
    const signupEmail = ref('')

    const handleSignup = async () => {
      loading.value = true
      signupError.value = ''
      
      try {
        const res = await store.dispatch('auth/signup', form.value)
        if (res.requiresValidation) {
          requiresOtpValidation.value = true
          signupEmail.value = res.email
          toast.info('Un code OTP vous a été envoyé par e-mail.')
        } else {
          toast.success('Inscription réussie ! Bienvenue sur Inveep GPAO.')
          router.push('/dashboard')
        }
      } catch (error) {
        signupError.value = error.message || 'Une erreur est survenue lors de la création du compte.'
        toast.error(signupError.value)
      } finally {
        loading.value = false
      }
    }

    const handleVerifyOtp = async () => {
      if (otpCode.value.length !== 6) {
        toast.error('Le code OTP doit contenir 6 chiffres.')
        return
      }

      loading.value = true
      signupError.value = ''

      try {
        await store.dispatch('auth/verifyOtp', {
          email: signupEmail.value,
          otpCode: otpCode.value
        })
        toast.success('Votre compte et entreprise ont été créés et validés avec succès !')
        router.push('/dashboard')
      } catch (error) {
        signupError.value = error.message
        toast.error(signupError.value)
      } finally {
        loading.value = false
      }
    }

    const handleResendOtp = async () => {
      loading.value = true
      try {
        await store.dispatch('auth/forgotPassword', { email: signupEmail.value })
        toast.success('Un nouveau code OTP a été envoyé à votre adresse e-mail.')
      } catch (error) {
        toast.error('Erreur lors du renvoi du code OTP.')
      } finally {
        loading.value = false
      }
    }

    return {
      form,
      loading,
      showPassword,
      signupError,
      requiresOtpValidation,
      otpCode,
      signupEmail,
      handleSignup,
      handleVerifyOtp,
      handleResendOtp
    }
  }
}
</script>

<style scoped>
.signup-container {
  min-height: 100vh;
  display: flex;
  position: relative;
  overflow: hidden;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
}

.signup-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #10b981, #059669);
  z-index: 1;
}

.background-shapes {
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.shape {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  animation: float 6s ease-in-out infinite;
}

.shape-1 {
  width: 300px;
  height: 300px;
  top: -10%;
  left: -5%;
  animation-delay: 0s;
}

.shape-2 {
  width: 400px;
  height: 400px;
  bottom: -15%;
  right: -5%;
  animation-delay: 2s;
}

.shape-3 {
  width: 200px;
  height: 200px;
  top: 50%;
  left: 45%;
  animation-delay: 4s;
}

@keyframes float {
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(180deg); }
  100% { transform: translateY(0px) rotate(360deg); }
}

.signup-content {
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 580px;
}

.signup-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
}

.signup-header {
  text-align: center;
  margin-bottom: 30px;
}

.logo {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 15px;
}

.logo-image {
  width: 70px;
  height: 70px;
  object-fit: contain;
  margin-bottom: 10px;
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
}

.logo h1 {
  font-size: 26px;
  font-weight: 700;
  color: #10b981;
  margin: 0;
}

.subtitle {
  color: #666;
  font-size: 14px;
  margin: 0;
}

.form-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.form-section h3 {
  font-size: 14px;
  font-weight: 600;
  color: #10b981;
  margin-top: 0;
  margin-bottom: 15px;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 8px;
}

.form-row {
  display: flex;
  gap: 15px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group.col {
  flex: 1;
}

label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-wrapper i {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  font-size: 14px;
}

.form-control {
  width: 100%;
  padding: 10px 12px 10px 36px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  transition: all 0.3s ease;
}

.form-control:focus {
  border-color: #10b981;
  outline: none;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
}

.password-toggle {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 0;
}

.btn-primary {
  background: linear-gradient(135deg, #10b981, #059669) !important;
  border: none !important;
  color: white;
  padding: 12px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  margin-top: 10px;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25) !important;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #059669, #047857) !important;
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(16, 185, 129, 0.35) !important;
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
}

.btn-outline {
  background-color: transparent;
  border: 2px solid #10b981;
  color: #10b981;
  font-weight: 600;
  padding: 12px;
  border-radius: 8px;
  transition: all 0.2s ease;
  width: 100%;
}

.btn-outline:hover:not(:disabled) {
  background-color: #10b981;
  color: white;
}

.alert {
  padding: 12px;
  border-radius: 6px;
  font-size: 13px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.alert-danger {
  background: #fef2f2;
  border: 1px solid #fca5a5;
  color: #b91c1c;
}

.signup-footer {
  text-align: center;
  margin-top: 25px;
  font-size: 13px;
  color: #666;
}

.signup-footer a {
  color: #10b981;
  text-decoration: none;
  font-weight: 600;
}

.signup-footer a:hover {
  text-decoration: underline;
}

.btn-outline {
  background: transparent;
  border: 2px solid #10b981;
  color: #10b981;
  padding: 12px;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
}

.btn-outline:hover:not(:disabled) {
  background: #10b981;
  color: white;
}

.btn-block {
  width: 100%;
}

.mt-3 {
  margin-top: 1rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.text-center {
  text-align: center;
}

.btn-link {
  background: none;
  border: none;
  color: #666;
  text-decoration: underline;
  cursor: pointer;
  font-size: 13px;
  width: 100%;
  margin-top: 8px;
}

.btn-link:hover {
  color: #333;
}
</style>
